import {useApiQuery, ApiClient, ConfigObjectType, useCurrentLocale} from '@wallaby-ui/core';
import {MerieSchema, useFormatters} from '@wallaby-ui/document';
import {useCallback, useMemo, useState} from 'react';

export type CombinationInstance = MerieSchema.components['schemas']['InstanceResponseDto'];
export type CombinationValue = MerieSchema.components['schemas']['ValueResponseDto'];
export type CombinationValueInput = MerieSchema.components['schemas']['CombinationValueDto'];
export type ShortcutResponse = MerieSchema.components['schemas']['ShortcutResponseDto'];
export type ShortcutDto = MerieSchema.components['schemas']['ShortcutDto'];

export type UseCombinationsProps = {
  config: ConfigObjectType;
  domain: string;
};

const DEFAULT_SHORTCUTS = [
  {position: 1, iconName: 'circleOkIcon', instanceLabel: 'hospitalization.detail.phRec.combination.shortcuts.system.drugStarted'},
  {position: 2, iconName: 'circleNokIcon', instanceLabel: 'hospitalization.detail.phRec.combination.shortcuts.system.drugStopped'},
  {
    position: 3,
    iconName: 'arrowDecreasingIcon',
    instanceLabel: 'hospitalization.detail.phRec.combination.shortcuts.system.drugDoseTooHigh',
  },
  {
    position: 4,
    iconName: 'arrowIncreasingIcon',
    instanceLabel: 'hospitalization.detail.phRec.combination.shortcuts.system.drugDoseTooLow',
  },
];

export function useCombinations({config, domain}: UseCombinationsProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const instancesUrl = config.service.url.combinationsInstancesList;
  const {
    data: instances = [],
    isLoading: instancesLoading,
    refetch: refetchInstances,
  } = useApiQuery<CombinationInstance[]>([instancesUrl, domain], null, {
    axiosConfig: {
      url: instancesUrl,
      params: {domain, includeValues: true},
    },
    reactQueryOptions: {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });

  const {t} = useFormatters();
  const locale = useCurrentLocale();

  const sortedInstances = useMemo(() => {
    const isNonEmpty = (s?: string | null) => !!s && s.trim().length > 0;

    const withOwner = instances
      .filter((i) => isNonEmpty(i.ownerId))
      .slice()
      .sort((a, b) =>
        t(a.label ?? '')
          .toLocaleLowerCase(locale)
          .localeCompare(t(b.label ?? '').toLocaleLowerCase(locale), locale, {
            sensitivity: 'base',
            numeric: true,
          })
      );

    const withoutOwner = instances.filter((i) => !isNonEmpty(i.ownerId));

    return [...withOwner, ...withoutOwner];
  }, [instances, locale, t]);

  const shortcutsUrl = config.service.url.combinationsShortcutsList;
  const {
    data: shortcuts = [],
    isLoading: shortcutsLoading,
    refetch: refetchShortcuts,
  } = useApiQuery<ShortcutResponse[]>([shortcutsUrl, domain], null, {
    axiosConfig: {
      url: shortcutsUrl,
      params: {domain},
    },
    reactQueryOptions: {
      enabled: !instancesLoading && instances.length > 0,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });

  const userOrSystemShortcuts = useMemo(() => {
    if (shortcuts.length > 0) {
      return shortcuts;
    }

    const defaultShortcutsData: ShortcutResponse[] = DEFAULT_SHORTCUTS.flatMap((s) => {
      const inst = instances.find((i) => i.label?.toLowerCase() === s.instanceLabel.toLowerCase() && i.ownerId == null);
      return inst?.id
        ? [
            {
              id: 0,
              combinationInstanceId: inst.id,
              combinationInstanceName: inst.label,
              iconName: s.iconName,
              position: s.position,
            },
          ]
        : [];
    });

    return defaultShortcutsData;
  }, [shortcuts, instances]);

  const findShortcutIconName = useCallback(
    (instanceId: number | undefined) =>
      (userOrSystemShortcuts || []).find((shortcut) => shortcut.combinationInstanceId === instanceId)?.iconName,
    [userOrSystemShortcuts]
  );

  const instanceOptions = useMemo(
    () =>
      sortedInstances.map((instance) => ({
        id: String(instance.id),
        label: instance.ownerId == null ? t(instance.label as string) : instance.label,
        iconName: findShortcutIconName(instance.id),
      })),
    [sortedInstances, t, findShortcutIconName]
  );

  const createInstance = useCallback(
    async (label: string, values: CombinationValueInput[], onSuccess?: (instanceId: number) => void) => {
      const createUrl = config.service.url.combinationsInstanceCreate;

      try {
        const response = await ApiClient.getData<CombinationInstance>(
          {
            url: createUrl,
            method: 'POST',
            data: {
              domain,
              label,
              values,
            },
          },
          true
        );

        await refetchInstances();

        if (onSuccess && response.id) {
          onSuccess(response.id);
        }

        return response;
      } catch (error) {
        console.error('Failed to create combination instance:', error);
        throw error;
      }
    },
    [config, domain, refetchInstances]
  );

  const updateInstance = useCallback(
    async (instanceId: number, label: string, values: CombinationValueInput[]) => {
      try {
        const updateLabelUrl = config.service.url.combinationsInstanceUpdate.replace('{id}', String(instanceId));
        await ApiClient.getData<CombinationInstance>(
          {
            url: updateLabelUrl,
            method: 'PUT',
            data: {label},
          },
          true
        );

        const valuesUrl = config.service.url.combinationsInstanceValues.replace('{id}', String(instanceId));
        const currentValues = await ApiClient.getData<CombinationValue[]>(
          {
            url: valuesUrl,
            method: 'GET',
          },
          true
        );

        if (currentValues.length > 0) {
          await Promise.all(
            currentValues.map((value) => {
              const deleteUrl = config.service.url.combinationsValueDelete
                .replace('{dimension}', value.key.dimension)
                .replace('{valueId}', String(value.key.id));

              return ApiClient.getData(
                {
                  url: deleteUrl,
                  method: 'DELETE',
                },
                true
              );
            })
          );
        }

        if (values.length > 0) {
          const addValuesUrl = config.service.url.combinationsInstanceAddValues.replace('{id}', String(instanceId));
          await ApiClient.getData(
            {
              url: addValuesUrl,
              method: 'POST',
              data: values,
            },
            true
          );
        }

        await refetchInstances();
        await refetchShortcuts();
      } catch (error) {
        console.error('Failed to update combination instance:', error);
        throw error;
      }
    },
    [config, refetchInstances, refetchShortcuts]
  );

  const deleteInstance = useCallback(
    async (instanceId: number) => {
      const deleteUrl = config.service.url.combinationsInstanceDelete.replace('{id}', String(instanceId));

      try {
        await ApiClient.getData(
          {
            url: deleteUrl,
            method: 'DELETE',
          },
          true
        );

        await refetchInstances();
        await refetchShortcuts();
      } catch (error) {
        console.error('Failed to delete combination instance:', error);
        throw error;
      }
    },
    [config, refetchInstances, refetchShortcuts]
  );

  const updateShortcuts = useCallback(
    async (
      shortcutsData: Array<{
        combinationInstanceId: number;
        iconName?: string;
        position?: number;
      }>
    ) => {
      const updateUrl = config.service.url.combinationsShortcutsReplace;

      try {
        const validShortcuts: ShortcutDto[] = shortcutsData
          .filter((s) => s.combinationInstanceId > 0 && s.iconName)
          .map((shortcut, index) => ({
            combinationInstanceId: shortcut.combinationInstanceId,
            iconName: shortcut.iconName,
            position: shortcut.position || index + 1,
          }));

        await ApiClient.getData<ShortcutResponse[]>(
          {
            url: updateUrl,
            method: 'PUT',
            data: {
              domain,
              shortcuts: validShortcuts,
            },
          },
          true
        );

        await refetchShortcuts();
      } catch (error) {
        console.error('Failed to update shortcuts:', error);
        throw error;
      }
    },
    [config, domain, refetchShortcuts]
  );

  return {
    // Data
    instances: sortedInstances,
    instanceOptions,
    instancesLoading,
    shortcuts: userOrSystemShortcuts,
    shortcutsLoading,

    // Actions
    createInstance,
    updateInstance,
    deleteInstance,
    refetchInstances,
    updateShortcuts,
    refetchShortcuts,

    // Dialog state
    showCreateDialog,
    openCreateDialog: () => setShowCreateDialog(true),
    closeCreateDialog: () => setShowCreateDialog(false),
  };
}
